import FingerprintJS from '@fingerprintjs/fingerprintjs';

/**
 * 设备指纹工具类
 *
 * 功能：
 * - 生成唯一的设备指纹ID
 * - 缓存指纹以提高性能
 * - 自动初始化和获取
 * - 提供降级方案
 *
 * @author YSFL Development Team
 * @since 2025-10-05
 */
class DeviceFingerprint {
  /** 单例实例 */
  private static instance: DeviceFingerprint;

  /** 缓存的设备ID */
  private cachedVisitorId: null | string = null;

  /** 指纹库Promise */
  private fpPromise: null | Promise<any> = null;

  /**
   * 私有构造函数，防止外部实例化
   * 使用单例模式确保全局只有一个实例
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  /**
   * 获取单例实例
   *
   * @returns DeviceFingerprint单例实例
   */
  public static getInstance(): DeviceFingerprint {
    if (!DeviceFingerprint.instance) {
      DeviceFingerprint.instance = new DeviceFingerprint();
    }
    return DeviceFingerprint.instance;
  }

  /**
   * 清除缓存的设备ID
   *
   * 用途：
   * - 测试多设备场景
   * - 用户主动更换设备
   * - 清除本地数据
   */
  public clearCache(): void {
    this.cachedVisitorId = null;
    try {
      localStorage.removeItem('device_id');
    } catch (error) {
      console.warn('无法清除localStorage中的设备ID:', error);
    }
  }

  /**
   * 获取设备指纹ID
   *
   * 流程：
   * 1. 检查内存缓存
   * 2. 初始化指纹库
   * 3. 获取指纹信息
   * 4. 缓存到内存和localStorage
   * 5. 失败时使用降级方案
   *
   * @returns Promise<string> 设备唯一标识符
   */
  public async getDeviceId(): Promise<string> {
    // 如果已有缓存，直接返回
    if (this.cachedVisitorId) {
      return this.cachedVisitorId;
    }

    // 优先从 localStorage 读取（确保一致性）
    try {
      const storedId = localStorage.getItem('device_id');
      if (storedId) {
        this.cachedVisitorId = storedId;
        return storedId;
      }
    } catch (error) {
      console.warn('[Device] 无法从 localStorage 读取:', error);
    }

    try {
      // 初始化指纹库
      if (!this.fpPromise) {
        this.fpPromise = FingerprintJS.load();
      }

      // 获取指纹实例
      const fp = await this.fpPromise;

      // 获取访问者信息
      // 这个过程会收集浏览器指纹信息：
      // - Canvas指纹
      // - WebGL指纹
      // - 音频指纹
      // - 字体列表
      // - 屏幕分辨率
      // - 时区
      // - 语言
      // 等多种信息
      const result = await fp.get();

      // 缓存指纹ID到内存
      this.cachedVisitorId = result.visitorId;

      // 存储到localStorage作为备份
      // 注意：localStorage可能被用户清除
      try {
        localStorage.setItem('device_id', result.visitorId);
      } catch (error) {
        // 某些浏览器禁用localStorage或存储空间已满
        console.warn('无法存储设备ID到localStorage:', error);
      }

      return result.visitorId;
    } catch (error) {
      console.error('获取设备指纹失败:', error);

      // 降级方案1：尝试从localStorage读取
      try {
        const storedId = localStorage.getItem('device_id');
        if (storedId) {
          this.cachedVisitorId = storedId;
          return storedId;
        }
      } catch (error_) {
        console.warn('无法从localStorage读取设备ID:', error_);
      }

      // 降级方案2：生成一个随机ID
      // 注意：这个ID不够稳定，仅作为最后的降级方案
      const fallbackId = `fallback-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      console.warn('使用降级设备ID（不推荐）:', fallbackId);

      // 尝试存储降级ID
      try {
        localStorage.setItem('device_id', fallbackId);
        this.cachedVisitorId = fallbackId;
      } catch {
        // 如果无法存储，只缓存在内存中
        this.cachedVisitorId = fallbackId;
      }

      return fallbackId;
    }
  }

  /**
   * 获取设备信息摘要
   *
   * 返回当前设备的基本信息，用于日志和调试
   *
   * @returns 设备信息对象
   */
  public getDeviceInfo(): {
    deviceId: null | string;
    language: string;
    platform: string;
    screen: string;
    userAgent: string;
  } {
    return {
      deviceId: this.cachedVisitorId,
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      screen: `${screen.width}x${screen.height}`,
      language: navigator.language,
    };
  }

  /**
   * 初始化指纹库
   *
   * 说明：
   * - 在应用启动时调用以预加载
   * - 加载过程耗时约100-200ms
   * - 预加载可提高首次获取速度
   *
   * @returns Promise<void>
   */
  public async initialize(): Promise<void> {
    if (!this.fpPromise) {
      this.fpPromise = FingerprintJS.load();
    }
    await this.fpPromise;
  }
}

// 导出单例实例
export const deviceFingerprint = DeviceFingerprint.getInstance();

/**
 * 快捷方法：获取设备ID
 *
 * 这是最常用的方法，直接获取设备指纹ID
 *
 * 使用示例：
 * ```typescript
 * import { getDeviceId } from '#/utils/device';
 *
 * const deviceId = await getDeviceId();
 * console.log('设备ID:', deviceId);
 * ```
 *
 * @returns Promise<string> 设备唯一标识符
 */
export async function getDeviceId(): Promise<string> {
  return deviceFingerprint.getDeviceId();
}

/**
 * 快捷方法：初始化设备指纹
 *
 * 建议在应用启动时调用，预加载指纹库
 *
 * 使用示例：
 * ```typescript
 * import { initializeDeviceFingerprint } from '#/utils/device';
 *
 * // 在main.ts中
 * await initializeDeviceFingerprint();
 * ```
 *
 * @returns Promise<void>
 */
export async function initializeDeviceFingerprint(): Promise<void> {
  return deviceFingerprint.initialize();
}

/**
 * 快捷方法：清除设备ID缓存
 *
 * 用于测试或用户主动清除数据
 *
 * 使用示例：
 * ```typescript
 * import { clearDeviceCache } from '#/utils/device';
 *
 * clearDeviceCache();
 * ```
 */
export function clearDeviceCache(): void {
  deviceFingerprint.clearCache();
}

/**
 * 快捷方法：获取设备信息
 *
 * 返回设备的基本信息，用于日志和调试
 *
 * @returns 设备信息对象
 */
export function getDeviceInfo() {
  return deviceFingerprint.getDeviceInfo();
}

// ============================================================================
// 会话ID管理
// ============================================================================

const SESSION_ID_KEY = 'bayes_mes_session_id';

/**
 * 生成会话ID
 *
 * 会话ID用于标识用户的一次访问会话
 * 页面刷新后会保持，但关闭浏览器后会重新生成
 */
function generateSessionId(): string {
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * 获取会话ID
 *
 * 从 sessionStorage 获取会话ID，如果不存在则生成新的
 *
 * @returns 会话ID
 */
export function getSessionId(): string {
  try {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = generateSessionId();
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  } catch {
    // sessionStorage 不可用时使用内存存储
    return generateSessionId();
  }
}
